/**
 * Reading an analysis run off the wire.
 *
 * The analysis endpoints answer with Server-Sent Events — `progress` ticks
 * while Claude writes, then a single `result` or `error` frame — because a run
 * takes minutes and a buffered response would sit on a proxy timeout. The
 * shared `api` wrapper can't be used for them: it buffers the body and caps
 * the request at 12s.
 *
 * Callers get one of two shapes back and never an exception: a run that failed
 * is a normal outcome here (the AI is down, the gateway isn't configured, the
 * stream cut out), and `reason` says which so the caller can word it for the
 * thing it was analysing.
 */

export type AnalysisProgress = { tokens: number; phase: "thinking" | "writing" };

export type AnalysisStreamFailure = {
  ok: false;
  /** `http` — the request was rejected before streaming started (`status` /
   *  `detail` carry the server's answer); `stream` — the run itself failed;
   *  `incomplete` — the stream ended without a result; `interrupted` — the
   *  connection died *after* the run had started; `timeout` — we gave up
   *  waiting; `network` — the request never got a response at all.
   *
   *  `interrupted` and `incomplete` are worth telling apart from `network`:
   *  the run was already underway server-side and may well have finished, so
   *  the honest answer is "check before running it again", not "nothing
   *  happened". */
  reason: "http" | "stream" | "incomplete" | "interrupted" | "timeout" | "network";
  status?: number;
  detail?: string;
};

export type AnalysisStreamResult<T> = { ok: true; result: T } | AnalysisStreamFailure;

/** A long meeting can genuinely take a while; this only guards a hung upstream. */
const DEFAULT_TIMEOUT_MS = 15 * 60_000;

export async function streamAnalysis<T>(
  url: string,
  body: unknown,
  opts: { onProgress?: (p: AnalysisProgress) => void; timeoutMs?: number } = {},
): Promise<AnalysisStreamResult<T>> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  let started = false;
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!res.ok) {
      const parsed = (await res.json().catch(() => null)) as {
        detail?: string;
        error?: string;
      } | null;
      const detail = parsed?.detail ?? parsed?.error;
      return { ok: false, reason: "http", status: res.status, ...(detail ? { detail } : {}) };
    }
    if (!res.body) return { ok: false, reason: "network" };

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let final: T | null = null;
    let streamErr: string | null = null;
    // Past this point the server is already running the analysis, so a
    // failure here is not "nothing happened" — see `interrupted` above.
    started = true;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx = buf.indexOf("\n\n");
      while (idx !== -1) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const payload = frame
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trim())
          .join("");
        if (payload) {
          const evt = JSON.parse(payload) as
            | ({ type: "progress" } & AnalysisProgress)
            | ({ type: "result" } & T)
            | { type: "error"; detail?: string };
          if (evt.type === "progress") {
            opts.onProgress?.({ tokens: evt.tokens, phase: evt.phase });
          } else if (evt.type === "result") {
            final = evt as T;
          } else {
            streamErr = evt.detail ?? "unknown error";
          }
        }
        idx = buf.indexOf("\n\n");
      }
    }

    if (streamErr !== null) return { ok: false, reason: "stream", detail: streamErr };
    if (!final) return { ok: false, reason: "incomplete" };
    return { ok: true, result: final };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return { ok: false, reason: "timeout" };
    }
    // A drop mid-stream (the server restarted, a proxy cut the connection, a
    // frame arrived malformed) is NOT the same as never being answered: the
    // run is underway and may have saved its result.
    return { ok: false, reason: started ? "interrupted" : "network" };
  } finally {
    clearTimeout(timeout);
  }
}
